import React, { FocusEventHandler, forwardRef, MouseEventHandler, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { PxToRem, Size, SIZES } from '../../fundamentals/sizes/sizes';
import { EoraSpace, IGutterAsMarginProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { FONT_SIZES, ITextAlignProps, useDefaultFontCss, useTextAlignCss } from '../../fundamentals/typography/typography';
import { STATUS_COLOURS } from '../../fundamentals/colours/variants';
import { Icon } from '../Icon/Icon';
import { ITextSize, Span } from '../Texts/Texts';
import { useCenteredFlexCss } from '../../fundamentals/display';
import { IInvalidProps, usePassedFocusCss } from '../../fundamentals/actions';
import { mergeRefs } from '../../common/utils';
import {
  useInputBorderCss,
  useInputFontSizeCss,
  useInputHeightCss,
  useInputWidthCss,
  useInputPaddingCss,
  useInputPlaceholderCss,
  useInputTextCss
} from './textfieldStyles';
import { IBorderRadiusProps } from '../../fundamentals/sizes/borderRadius';
import { IFullWidthProps, useFullWidthCss } from '../../fundamentals/breakpoints/breakpoints';
import { IEndIconProps, IStartIconProps } from '../../common/interfaces';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseInput, BaseDiv } from '../../common/BaseElements';
import { interactivityColour } from '../../fundamentals/colours/eoraColourUtils';

export const textFieldColours = ({ text, eoraCoreColours }: IColoursCollection) => ({
  textFieldColours: {
    default: {
      border: `1px solid ${eoraCoreColours.BORDER_COLOR.COLOR_BORDER_DEFAULT}`,
      background: eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND,
      text: text.default
    },
    [STATUS_COLOURS.ERROR]: {
      border: `1px solid ${eoraCoreColours.BORDER_COLOR.COLOR_BORDER_ERROR}`
    },
    placeholder: {
      text: text.light
    },
    disabled: {
      border: `1px solid ${eoraCoreColours.BORDER_COLOR.COLOR_BORDER_DISABLED}`,
      background: interactivityColour(eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_3, eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND),
      text: text.disabled
    }
  }
});

// Note: copy from HTMLInputTypeAttribute
// type=number might require more styling
type TextFieldTypeAttribute =
  // | 'button' | 'checkbox' | 'color' | 'date' | 'datetime-local' | 'file' | 'hidden' | 'image' | 'month' | 'radio' | 'range' | 'reset' | 'search' | 'submit' | 'time' | 'week' | (string & {})
  'email' | 'number' | 'password' | 'tel' | 'text' | 'url';

export type InputSize = typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE;
export interface IInputSizeProps {
  inputSize?: InputSize;
}

export type InputWidth = typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE | typeof SIZES.X_LARGE;
export interface IInputWidthProps {
  width?: InputWidth | number;
}

export interface ITextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'width'>,
    IGutterAsMarginProps,
    IInvalidProps,
    IInputSizeProps,
    IInputWidthProps,
    ITextAlignProps,
    IFullWidthProps,
    IStartIconProps,
    IEndIconProps {
  type?: TextFieldTypeAttribute;
  startText?: string; // Note: can we convert this params to accept JSX and combine with icon below
  endText?: string;
}

export const inputFontSizeMapping: Record<InputSize, ITextSize> = {
  [SIZES.SMALL]: FONT_SIZES.TEXT_SMALL,
  [SIZES.MEDIUM]: FONT_SIZES.TEXT_DEFAULT,
  [SIZES.LARGE]: FONT_SIZES.TEXT_LARGE
};

export const inputIconSizes: Record<InputSize, Size> = {
  [SIZES.SMALL]: SIZES.MICRO,
  [SIZES.MEDIUM]: SIZES.XXX_SMALL,
  [SIZES.LARGE]: SIZES.XX_SMALL
};

const textFieldPads = (eoraSpaces: Record<EoraSpace, PxToRem>): Record<InputSize, string> => ({
  [SIZES.SMALL]: `${eoraSpaces.offgrid_6} ${eoraSpaces.medium}`,
  [SIZES.MEDIUM]: `${eoraSpaces.medium} ${eoraSpaces.offgrid_12}`,
  [SIZES.LARGE]: `${eoraSpaces.offgrid_10} ${eoraSpaces.large}`
});

export const DEFAULT_INPUT_SIZE = SIZES.MEDIUM;
export const DEFAULT_INPUT_WIDTH = SIZES.LARGE;

const useClearWebkitCss = css`
  &:-webkit-autofill::first-line,
  &:-webkit-autofill {
    ${useDefaultFontCss};
  }

  ::-ms-reveal {
    display: none;
  }

  /* Required css to remove spinner on Webkit browsers */
  &&::-webkit-outer-spin-button,
  &&::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`;

export const useInputResetCss = css`
  appearance: none;
  color: inherit;
  border: none;
  background: transparent;
  width: inherit;
  padding: 0;

  &:focus {
    outline-style: none;
    box-shadow: none;
    border-color: transparent;
  }
  caret-color: ${({ theme }) => theme.colours.text.default};

  ${useClearWebkitCss}
`;

export type ITextFieldCssProps = Omit<ITextFieldProps, 'width'> & {
  styleWidth?: IInputWidthProps['width'];
};

export const TextFieldBase = styled(BaseInput)<ITextFieldCssProps>`
  ${useInputResetCss};
  ${useInputFontSizeCss};
  ${useTextAlignCss};

  width: 100%;

  &::placeholder {
    ${useInputPlaceholderCss};
  }

  ${({ inputMode }) =>
    inputMode === 'numeric' &&
    css`
      appearance: textfield;
    `}
`;

const ExtraPlaceholderText = styled(Span)<ITextFieldProps>`
  color: ${({ disabled, theme }) => theme.textFieldColours[disabled ? `disabled` : `placeholder`].text};
  cursor: default;
`;

const TextFieldContainer = styled(BaseDiv)<ITextFieldCssProps & { focused?: boolean } & IBorderRadiusProps>`
  ${useCenteredFlexCss};
  ${useGutterAsMarginCss()};
  ${({ maxLength }) => maxLength !== 1 && useInputPaddingCss};
  ${useInputHeightCss};
  ${useInputTextCss};

  padding: ${({ theme, inputSize }) => textFieldPads(theme.eoraSpaces)[inputSize!]};
  background: ${({ theme, disabled }) => theme.textFieldColours[disabled ? `disabled` : `default`].background};
  border-radius: ${({ theme }) => theme.brandBorderRadius.input};

  ${useInputWidthCss()};
  ${useInputBorderCss};
  ${usePassedFocusCss};
  ${useFullWidthCss};
`;

export const TextField = forwardRef<HTMLInputElement, ITextFieldProps>(
  (
    {
      startIcon,
      startText,
      endIcon,
      endText,
      type = 'text',
      inputSize = DEFAULT_INPUT_SIZE,
      startIconType,
      endIconType,
      onStartIconClick,
      onEndIconClick,
      disabled,
      className,
      style,
      fullWidth,
      width = DEFAULT_INPUT_WIDTH,
      ...rest
    },
    ref
  ) => {
    const { generateTestId } = useTestId(rest, 'Input');

    const [inputFocused, setInputFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleOnInputFocus: FocusEventHandler<HTMLInputElement> = (e) => {
      rest.onFocus?.(e);
      setInputFocused(true);
    };

    const handleOnInputOnBlur: FocusEventHandler<HTMLInputElement> = (e) => {
      rest.onBlur?.(e);
      setInputFocused(false);
    };

    const handleContainerOnClick: MouseEventHandler<HTMLInputElement> = () => {
      inputRef?.current?.focus();
    };

    const theme = useTheme();
    const colour = theme.textFieldColours[disabled ? `disabled` : `placeholder`].text;
    const inputMode = type === 'number' ? (rest.inputMode ?? 'numeric') : rest.inputMode;

    const { gutter, gutterBottom, gutterLeft, gutterRight, gutterTop, invalid, maxLength } = rest;
    const containerProps = { disabled, gutter, gutterBottom, gutterLeft, gutterRight, gutterTop, invalid, maxLength };

    return (
      <TextFieldContainer
        focused={inputFocused}
        inputSize={inputSize}
        {...generateTestId(`root`)}
        {...containerProps}
        className={className}
        style={style}
        fullWidth={fullWidth}
        styleWidth={width}
        onClick={handleContainerOnClick}
      >
        {!!startIcon && (
          <Icon
            colour={colour}
            gutterRight={8}
            name={startIcon}
            size={inputIconSizes[inputSize!]}
            {...generateTestId(`icon`)}
            type={startIconType}
            onClick={onStartIconClick}
          />
        )}
        {!!startText && (
          <ExtraPlaceholderText disabled={disabled} gutterRight={8} textSize={inputFontSizeMapping[inputSize!]!} {...generateTestId(`startText`)}>
            {startText}
          </ExtraPlaceholderText>
        )}
        <TextFieldBase
          ref={mergeRefs([inputRef, ref])}
          type={type === 'number' ? 'text' : type}
          inputSize={inputSize}
          disabled={disabled}
          {...generateTestId(type)}
          {...rest}
          inputMode={inputMode}
          onFocus={handleOnInputFocus}
          onBlur={handleOnInputOnBlur}
        />
        {!!endText && (
          <ExtraPlaceholderText disabled={disabled} gutterLeft={8} textSize={inputFontSizeMapping[inputSize!]!} {...generateTestId(`endText`)}>
            {endText}
          </ExtraPlaceholderText>
        )}
        {!!endIcon && (
          <Icon
            colour={colour}
            gutterLeft={8}
            name={endIcon}
            size={inputIconSizes[inputSize!]}
            {...generateTestId(`endIcon`)}
            type={endIconType}
            onClick={onEndIconClick}
          />
        )}
      </TextFieldContainer>
    );
  }
);
